// @ts-nocheck
import { useLayoutEffect, useRef, useState, useEffect } from 'react'
import styles from './search.module.scss'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import {
  getAuthors,
  getLead,
  getPublishingDate,
  getTeaserImgSrc,
  getTitle,
  getTitleHeader,
  getUpdatedDate,
} from '@ta-interaktiv/api-access'
import styled from 'styled-components'
import { Search as SearchIcon } from '@styled-icons/bootstrap/Search'
import { ExternalLink } from '@styled-icons/evaicons-solid/ExternalLink'
import { GlobalVariables } from 'Globals/GlobalVariables'

export const Search = ({ ids }) => {
  const input = useRef()
  const [articles, setArticles] = useState([])
  const [filteredArticles, setFilteredArticles] = useState([])
  const [showResult, setShowResult] = useState(false)
  const domain =
    parseHostname().publicationName === 'tagesanzeiger'
      ? '24heures'
      : parseHostname().publicationName

  const getArticleTeaserData = (
    domain: string,
    articleId: string | number
  ): Promise<ArticleTeaserData> => {
    return Promise.all([
      getAuthors(domain, articleId),
      getTeaserImgSrc(domain, articleId, '800px'),
      getLead(domain, articleId),
      getTitle(domain, articleId),
      getTitleHeader(domain, articleId),
      getPublishingDate(domain, articleId),
      getUpdatedDate(domain, articleId),
    ])
      .then(
        ([
          authors,
          teaserImgSrc,
          lead,
          title,
          titleHeader,
          publishingDateAsIsoString,
          updatedDateAsIsoString,
        ]: [string[], string, string, string, string, string, string]) => {
          return {
            authors: authors.join(', '),
            imageSrc: teaserImgSrc,
            lead: lead ?? '',
            title: title ?? '',
            titleHeader: titleHeader,
            publishingDate: new Date(publishingDateAsIsoString).getTime(),
            updateDate: new Date(updatedDateAsIsoString).getTime(),
            id: articleId,
          }
        }
      )
      .catch((e: Error) => {
        console.warn(
          `Article with ID ${articleId} could not be loaded, due to ${e.name}: ${e.message}`
        )
      })
  }

  const handleInputChange = (value) => {
    const userValue = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()

    const result =
      value.length > 2
        ? [...articles.values()].filter(
            (a) =>
              a.title
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .indexOf(userValue) !== -1 ||
              a.lead
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .indexOf(userValue) !== -1
          )
        : []
    if (value.length > 2 && !showResult) {
      setShowResult(true)
    } else if (value.length <= 2) {
      setShowResult(false)
    }
    setFilteredArticles(result)
  }

  useEffect(() => {
    ids.forEach((articleId) => {
      getArticleTeaserData(domain, articleId).then(
        (articleTeaserData: ArticleData) => {
          if (articleTeaserData) {
            setArticles((articles) =>
              new Map(
                [...articles].sort((a, b) => b[1].updateDate - a[1].updateDate)
              ).set(articleId, articleTeaserData)
            )
          }
        }
      )
    })
  }, [ids])

  return (
    <div>
      <SearchContainer>
        <InputContainer>
          <Input
            type='search'
            placeholder='Effectuez une recherche'
            ref={input}
            onChange={(e) => {
              handleInputChange(e.target.value)
            }}
          />
          <SearchButton>
            <SearchIcon size={24} />
          </SearchButton>
        </InputContainer>
      </SearchContainer>
      {showResult && (
        <ResultsContainer>
          <div className={styles.resultLabel}>
            {filteredArticles.length === 0
              ? 'Aucun résultat'
              : filteredArticles.length +
                (' résultat' + (filteredArticles.length > 1 ? 's' : ''))}
          </div>
          <div>
            {filteredArticles.map((a, i) => (
              <Item key={`result-item-${i}`}>
                <img src={a.imageSrc} alt={a.title} />
                <div>
                  <div>{a.title}</div>
                  <div className={styles.link}>
                    <a href={`https://www.${domain}.ch/${a.id}`}>
                      <ExternalLink size={20} /> Lire l'article
                    </a>
                  </div>
                </div>
              </Item>
            ))}
          </div>
        </ResultsContainer>
      )}
    </div>
  )
}

const SearchContainer = styled(GlobalVariables)`
  font-family: var(--font-plex);
  font-size: 1.8rem;
  color: var(--stda-light-yellow);
  margin: 1rem;
`
const InputContainer = styled.div`
  position: relative;
  width: 100%;
`
const Input = styled.input`
  background-color: transparent;
  border: 1px solid #f1e2ce;
  padding: 0.85em 1em;
  padding-left: 60px;
  border-radius: 50px;
  outline: none;
  color: var(--stda-light-yellow);
  width: 100%;
`
const SearchButton = styled.div`
  position: absolute;
  left: 30px;
  top: 50%;
  margin-top: -24px;
  background-color: transparent;
  border-radius: 50px;
  width: 46px;
  height: 46px;
  line-height: 46px;
`

const ResultsContainer = styled(GlobalVariables)`
  margin: 1rem 0;
  padding: 1rem;
  font-size: 1.6rem;
  color: var(--stda-light-yellow) !important;
  a {
    color: var(--text-color);
  }
`
const Item = styled.div`
  line-height: 1.2;
  font-family: var(--font-noto);
  color: var(--stda-light-yellow);
  font-weight: 600;
  margin-top: 1em;
  padding-bottom: 1em;
  border-bottom: 1px solid #f1e2ce22;
  display: grid;
  grid-template-columns: 100px 1fr;
  column-gap: 1em;
  -webkit-font-smoothing: antialiased;

  img {
    width: 100px;
    border-radius: 12px;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
  }
`
